import { useAuth } from 'src/auth/context'
import type { IUser } from 'src/types/User'

/**
 * The session in the shape the data layer wants: a bearer token, the identity,
 * and whether either is still settling.
 *
 * This is the seam the old `useAuth0Token` occupied. It stays because callers
 * only ever wanted those three things, and keeping the shape means the query
 * hooks did not have to change when the identity provider did.
 */
export const useAuthToken = (): {
  token: string | undefined
  accessToken: string | undefined
  idToken: string | undefined
  user: IUser | undefined
  isLoading: boolean
  error: Error | undefined
} => {
  const { accessToken, idToken, session, isLoading, error } = useAuth()

  // `sub` is the join key to cfg.user.external_id, and is the only field the
  // portal actually reads. Migrated identities keep their original `auth0|…`
  // subject, so existing rows continue to resolve.
  const user: IUser | undefined = session && {
    sub: session.sub,
    email: session.email,
    nickname: session.name ?? undefined,
    picture: session.picture ?? undefined,
    company: session.platform?.companyName ?? undefined,
  }

  return { token: accessToken, accessToken, idToken, user, isLoading, error }
}
